import { useCallback, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import DocumentationIconButton from "components/DocumentationIconButton";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import CardCollapsible from "ds/components/Card/Collapsible";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import { generateCallbackUrl } from "shared/Templates/utils";
import { Blueprint, BlueprintState } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import DeploymentForm from "../components/DeploymentForm";
import { VersionTemplateFormFields } from "../components/DeploymentForm/types";
import useDeploymentForm from "../components/DeploymentForm/useDeploymentForm";
import { showDeployConfirmationModal } from "./DeployConfirmationModal";
import styles from "./styles.module.css";

type DeployFormProps = {
  version: Blueprint;
  templateName: string;
  templateId: string;
  isFromMarketplace?: boolean;
};

const DeployForm = ({ version, templateName, templateId, isFromMarketplace }: DeployFormProps) => {
  const navigate = useNavigate();

  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(false);

  const deploymentInstructions = version.instructions;

  const { builderForm, onSubmit, stackCreateLoading, hasErrors, hasPreview, errors, inputs } =
    useDeploymentForm({
      version,
      submitCallback: (deploymentId) =>
        navigate(generateCallbackUrl(isFromMarketplace, templateId, version.id, deploymentId)),
    });

  const handleOpenFullDescription = () => {
    showFullDescription({ description: version.description });
  };

  const {
    handleSubmit,
    formState: { isDirty },
    register,
  } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const handleDeploy: SubmitHandler<VersionTemplateFormFields> = useCallback(
    async (formData) => {
      const isValid = await runFormValidations();
      if (!isValid) return;

      showDeployConfirmationModal({
        templateName,
        deploymentName: formData.name,
        onDeploy: () => onSubmit(formData),
      });
    },
    [templateName, onSubmit, runFormValidations]
  );

  return (
    <FormProvider {...builderForm}>
      <form onSubmit={handleSubmit(handleDeploy)}>
        <FullScreenModalBody noMaxWidth>
          <Box justify="center" gap="x-large" className={styles.contentWrapper} fullWidth>
            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              align="center"
              className={styles.formSection}
            >
              <Box direction="column" className={styles.formFields}>
                <DeploymentForm
                  item={version}
                  isPublished={version.state === BlueprintState.Published}
                  hasPreview={hasPreview}
                  hasErrors={hasErrors}
                  errors={errors}
                  inputs={inputs}
                  register={register}
                  onOpenFullDescription={handleOpenFullDescription}
                />
              </Box>
            </Box>

            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              className={styles.instructionsSection}
            >
              <CardCollapsible
                title="Instructions"
                isCollapsed={isInstructionsCollapsed}
                onToggle={() => setIsInstructionsCollapsed(!isInstructionsCollapsed)}
                ariaLevel={2}
                ariaLabel="Deployment instructions"
              >
                {deploymentInstructions ? (
                  <Markdown markup={deploymentInstructions} />
                ) : (
                  <Typography tag="div" variant="p-body2" color="secondary">
                    No deployment instructions provided for this template.
                  </Typography>
                )}
              </CardCollapsible>
            </Box>
          </Box>
        </FullScreenModalBody>

        <FullScreenModalFooter justify="between">
          {/* TODO: placeholder link */}
          <DocumentationIconButton
            href={getDocsUrl("/concepts/template/deployments")}
            tooltipText="Go to templates documentation"
            size="small"
          />
          <Button
            variant="primary"
            type="submit"
            loading={stackCreateLoading}
            disabled={stackCreateLoading || !isDirty}
          >
            Deploy
          </Button>
        </FullScreenModalFooter>
      </form>
    </FormProvider>
  );
};

export default DeployForm;
